import axios, { AxiosError, AxiosRequestConfig } from "axios";
import useSWR, { SWRConfiguration } from "swr";

import generalRequestErrorHandler from "./generalRequestErrorHandler";

interface IApiRequestGetConfig<TResponse> {
	url: string | null;
	axiosConfig?: AxiosRequestConfig;
	swrConfig?: SWRConfiguration<TResponse, AxiosError>;
	onError?: (error: AxiosError | Error) => void;
}

interface IApiRequestGetResult<TResponse> {
	data: TResponse | undefined;
	isLoading: boolean;
	isValidating: boolean;
	error: AxiosError | undefined;
	getUpdateData: ReturnType<typeof useSWR<TResponse, AxiosError>>["mutate"];
}

const fetcher = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
	try {
		await new Promise((resolve) => setTimeout(resolve, 2000));
		const response = await axios.get<T>(url, config);

		return response.data;
	} catch (error: any) {
		generalRequestErrorHandler(error as AxiosError);
		throw error;
	}
};

const useApiRequestGet = <TResponse = any>(
	config: IApiRequestGetConfig<TResponse>
): IApiRequestGetResult<TResponse> => {
	const { data, error, isLoading, mutate, isValidating } = useSWR<TResponse, AxiosError>(
		config.url,
		config.url ? (url) => fetcher<TResponse>(url, config.axiosConfig) : null,
		{
			...config.swrConfig,
			onError: (err) => {
				if (config.onError) {
					config.onError(err);
				}
			},
		}
	);

	return {
		data,
		isLoading,
		isValidating,
		error,
		getUpdateData: mutate,
	};
};

export default useApiRequestGet;
